import type {
  MultipleQueriesQuery,
  SearchResponse,
} from '@algolia/client-search'
import {
  type AlgoliaDomain,
  useAlgoliaSearchOptions,
} from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import algoliasearch, { type AlgoliaSearchOptions } from 'algoliasearch'

export type DefaultAlgoliaResults = { results: { hits: unknown[] }[] }

export type AlgoliaSearchFn<T = unknown> = (
  queries: MultipleQueriesQuery[],
) => Promise<{ results: Array<SearchResponse<T>> }>

export type InitializableAlgoliaClient<T> = (
  id: string,
  apiKey: string,
  options?: AlgoliaSearchOptions,
) => { search: AlgoliaSearchFn<T> }

export type AlgoliaSearchParams = {
  queries: MultipleQueriesQuery[]
  apiKey: string
}

/**
 * This allows to inject a fake algolia client to easily test the feature
 * - without using the Algolia library (and making http calls)
 * - without mocking imports
 * @algolia
 * @returns
 */
export function useAlgoliaWithDeps<T>({
  initializableAlgoliaClient,
  algoliaSearchOptionType,
}: {
  initializableAlgoliaClient: InitializableAlgoliaClient<T>
  algoliaSearchOptionType: AlgoliaDomain
}) {
  const algoliaSearchOption = useAlgoliaSearchOptions(algoliaSearchOptionType)

  function search(params: AlgoliaSearchParams) {
    const algoliaClient = initializableAlgoliaClient(
      'dummy-app-id',
      'dummy-key',
      algoliaSearchOption,
    )

    return algoliaClient.search(params.queries)
  }

  return { search }
}

export function useAlgolia<T>(
  algoliaSearchOptionType: AlgoliaDomain = 'compare',
) {
  return useAlgoliaWithDeps({
    initializableAlgoliaClient: algoliasearch as InitializableAlgoliaClient<T>,
    algoliaSearchOptionType,
  })
}
